import { useId, useState } from 'react';
import { AlertTriangle, ChevronDown } from 'lucide-react';
import type { PorRegimeDto, PorRegimeEtapaResponse, ReformaFormacaoResponse } from '@triade/contracts';
import { NOTA_INFORMATIVO, REGIME_ROTULO, SeloEstimada, TabelaTributos, ValoresReforma } from '@/components/reforma/reforma-ui';
import { formatarData } from '@/lib/format';
import { cn } from '@/lib/utils';
import { PainelPorRegime } from './painel-por-regime';

interface Props {
  /** Dados de reforma da formacao (null quando ainda nao ha simulacao). */
  reforma: ReformaFormacaoResponse | null;
  /** Dto do "Preco por regime"; null nao mostra o painel. A consulta so roda com a secao aberta. */
  porRegimeDto: PorRegimeDto | null;
}

/**
 * Secao "Reforma tributária" da formacao: informativa, recolhida por padrao.
 * Presentacional: todos os valores vem prontos da API.
 */
export function BlocoReformaFormacao({ reforma, porRegimeDto }: Props) {
  const [aberto, setAberto] = useState(false);
  // Linha do "Preço por regime" carregada no topo (clique). Nula = a configuracao da data da operacao (padrao: ano corrente).
  const [carregada, setCarregada] = useState<PorRegimeEtapaResponse | null>(null);
  const idConteudo = useId();

  return (
    <section aria-label="Reforma tributária" data-testid="reforma-formacao" className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-5">
      <h2 className="text-title font-semibold">
        <button
          type="button"
          aria-expanded={aberto}
          aria-controls={idConteudo}
          onClick={() => setAberto((v) => !v)}
          className="flex w-full flex-wrap items-center gap-2 rounded-lg text-left focus-visible:outline-none focus-visible:shadow-focus"
        >
          <span>Reforma tributária</span>
          {reforma && (
            <span className="rounded-full bg-line-soft px-2.5 py-0.5 text-caption font-medium text-ink-body">
              {reforma.etapa} · {REGIME_ROTULO[reforma.regime]}
            </span>
          )}
          {reforma?.aliquotaEstimada && <SeloEstimada />}
          {!aberto && <span className="text-caption font-normal text-ink-muted">Informativo · clique para ver</span>}
          <ChevronDown aria-hidden="true" className={cn('ml-auto size-4 shrink-0 text-ink-muted transition-transform', aberto && 'rotate-180')} />
        </button>
      </h2>

      <div id={idConteudo} hidden={!aberto} className="flex flex-col gap-3">
        {aberto && reforma?.informativo && <p className="text-caption text-ink-muted">{NOTA_INFORMATIVO}</p>}
        {aberto && reforma?.precoIncluiCbsIbs && <p className="text-caption text-ink-muted">O praticado inclui CBS/IBS.</p>}

        {aberto && reforma && (
          <>
            <div data-testid="reforma-ano-carregado" className="flex flex-wrap items-center gap-2 text-small">
              <span className="font-medium text-ink-body">Ano carregado: {(carregada?.data ?? reforma.data).slice(0, 4)}</span>
              <span className="text-ink-muted">
                {carregada
                  ? `${carregada.etapa} · ${REGIME_ROTULO[carregada.regime]} · a partir de ${formatarData(carregada.data)}`
                  : (reforma.data.slice(0, 4) === String(new Date().getFullYear()) ? 'ano corrente' : 'data da operação')}
              </span>
              {carregada && (
                <button type="button" onClick={() => setCarregada(null)} className="rounded-md text-caption font-medium text-primary hover:underline focus-visible:outline-none focus-visible:shadow-focus">
                  Voltar ao ano corrente
                </button>
              )}
              {carregada?.aliquotaEstimada && <SeloEstimada />}
            </div>

            <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-5">
              <ValoresReforma titulo="Sugerido" valores={(carregada ?? reforma).sugerido} informativo={carregada ? carregada.regime === 'DUAL_2026' : reforma.informativo} />
              {(carregada ? carregada.praticado : reforma.praticado) && (
                <ValoresReforma
                  titulo="Praticado"
                  valores={(carregada ? carregada.praticado : reforma.praticado) as NonNullable<typeof reforma.praticado>}
                  informativo={carregada ? carregada.regime === 'DUAL_2026' : reforma.informativo}
                />
              )}
            </div>

            {carregada?.erro && (
              <p role="alert" className="text-small text-danger-ink">
                {carregada.erro.message} <span className="text-ink-muted">({carregada.erro.code})</span>
              </p>
            )}

            <TabelaTributos tributos={(carregada ?? reforma).sugerido.tributos} />

            {!carregada &&
              reforma.avisos.map((a) => (
                <div
                  key={a.code}
                  role="alert"
                  data-testid={`reforma-aviso-${a.code}`}
                  className="flex items-start gap-2 rounded-xl border border-warning-dot/40 bg-warning-bg px-3.5 py-2.5 text-small text-ink-body"
                >
                  <AlertTriangle aria-hidden="true" className="mt-px size-4 shrink-0 text-warning-ink" />
                  <span>{a.message}</span>
                </div>
              ))}
          </>
        )}

        {aberto && porRegimeDto !== null && (
          <PainelPorRegime dto={porRegimeDto} embutido onSelecionar={reforma ? setCarregada : undefined} selecionada={carregada ? `${carregada.etapa}-${carregada.data}` : null} />
        )}
      </div>
    </section>
  );
}
